'use client';

import { useMemo, useState } from 'react';

import { useAuthentication, UserGroups } from '@ravago/shared/authentication';
import { downloadProductComparisonDocuments } from '@ravago/shared/next-data-access/client';
import { NormalizedProduct } from '@ravago/shared/page-data/models/contentful-mapped';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import notification from '@ravago/shared/radiance/utils/notification/notification';
import { useTranslations } from '@ravago/shared/translations/client';

import { saveAs } from 'file-saver';

import { useDialogContext } from '../../../../hooks/use-dialog-context';
import { CorrelationService } from '../../../../services/tracking/correlation-token.service';

interface Props {
   products: NormalizedProduct[];
   getShareableUrl: () => string;
   getProspectorUrlForSellers: (products: NormalizedProduct[]) => Promise<string | undefined>;
}

export const ProductCompareActions = ({ products, getShareableUrl, getProspectorUrlForSellers }: Props) => {
   const translate = useTranslations();
   const { user } = useAuthentication();
   const { dispatch } = useDialogContext();
   const { phone } = useBreakpoint();

   const [isDownloading, setIsDownloading] = useState(false);

   const isSalesRep = useMemo(
      () => user?.groups?.includes(UserGroups.ravagoSalesRepresentative) || user?.groups?.includes(UserGroups.ravagoGlobalGroupAdmin),
      [user]
   );

   const isDownloadDisabled = useMemo(() => products.every((product) => !product.ulProductId), [products]);

   const handleOpenShareDialog = () => {
      if (dispatch) {
         dispatch({
            type: 'open_dialog',
            value: {
               dialogType: 'share-product-compare',
               shareUrl: getShareableUrl()
            }
         });
      }
   };

   const handleViewDetailedComparison = async () => {
      if (!isSalesRep) return;
      try {
         const url = await getProspectorUrlForSellers(products);
         if (url) window.open(url, '_blank');
      } catch {
         notification(translate('shipment.documents.download-error'));
      }
   };

   const handleDownload = async () => {
      if (isDownloading) return;

      setIsDownloading(true);

      try {
         const productIds = products
            .map((product) => product.ulProductId)
            .filter((ulProductId): ulProductId is number => ulProductId != null)
            .map((ulProductId) => ulProductId.toString());

         const correlationId = CorrelationService.generateCorrelationId('dpcid');
         const blob = await downloadProductComparisonDocuments(productIds, correlationId);
         saveAs(blob, 'CompareProducts.pdf');
      } catch {
         notification(translate('shipment.documents.download-error'));
      } finally {
         setIsDownloading(false);
      }
   };

   if (products.length === 0) return null;

   return (
      <Box direction={phone ? 'column' : 'row'} gap="sm" items="center">
         <Button variant="secondary" leadingIcon="share" onClick={handleOpenShareDialog} fullWidth={phone}>
            {translate('page-consumer.product-compare.share-comparison')}
         </Button>
         <Button
            variant="secondary"
            leadingIcon={'download'}
            onClick={handleDownload}
            disabled={isDownloadDisabled || isDownloading}
            fullWidth={phone}
         >
            {translate('page-consumer.product-compare.download-detailed-comparison')}
         </Button>
         {isSalesRep && (
            <Button variant="secondary" leadingIcon="arrow-up-right-from-square" onClick={handleViewDetailedComparison} fullWidth={phone}>
               {translate('page-consumer.product-compare.view-detailed-comparison')}
            </Button>
         )}
      </Box>
   );
};
